import React from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { DatePicker } from "@/components/ui/date-picker";
import { Paperclip, ListChecks, MessageSquare, Flag, Check } from "lucide-react";
import { fmtDate, isOverdue } from "@/lib/constants";
import { STATUS_PILL, STATUS_CHOICES, PRIORITY_FLAG, PRIORITY_CHOICES, folderTag, toneFor } from "@/lib/dashboardTheme";

const Initials = ({ name, id, i }) => (
  <span title={name} style={{ background: toneFor(id), marginLeft: i ? -8 : 0 }}
    className="inline-flex items-center justify-center h-6 w-6 rounded-full text-[10px] font-semibold text-white ring-2 ring-white shrink-0">
    {(name || "?").trim().charAt(0).toUpperCase()}
  </span>
);

export default function DashboardTaskRow({ task, members, onOpenTask, onChanged, canManage }) {
  const navigate = useNavigate();
  const overdue = isOverdue(task);
  const pill = STATUS_PILL[task.status] || STATUS_PILL.assigned;
  const tag = folderTag(task.category);
  const prio = PRIORITY_FLAG[task.priority] || PRIORITY_FLAG.none;

  const save = async (patch) => {
    const prev = { ...task };
    onChanged(task.task_id, patch); // optimistic
    try { await api.patch(`/tasks/${task.task_id}`, patch); }
    catch { onChanged(task.task_id, prev); }
  };
  const toggleAssignee = (uid) => {
    const cur = task.assigned_to || [];
    const next = cur.includes(uid) ? cur.filter((x) => x !== uid) : [...cur, uid];
    save({ assigned_to: next });
  };

  const dueColor = overdue ? "#B42318" : (task.end_date && task.status !== "completed" ? "#B7791F" : "var(--twp-text-2)");

  return (
    <div className="grid grid-cols-[minmax(0,1fr)_auto_auto] lg:grid-cols-[minmax(0,1fr)_140px_90px_92px_120px_120px_56px_120px] gap-3 items-center px-4 py-2.5 border-b hover:bg-[var(--twp-row-hover)] transition-colors"
      style={{ borderColor: "var(--twp-divider)" }} data-testid={`board-row-${task.task_id}`}>
      {/* Name + breadcrumb + icons */}
      <div className="text-left min-w-0">
        <p className="text-[10px] uppercase tracking-[0.04em] truncate flex items-center gap-1" style={{ color: "var(--twp-muted)" }}>
          <span className="inline-block h-1.5 w-1.5 rounded-full shrink-0" style={{ background: tag.dot }} />
          <button
            onClick={(e) => { e.stopPropagation(); navigate(`/project/${task.project_id}`); }}
            className="truncate hover:underline font-semibold"
            style={{ color: "var(--twp-text-2)" }}
            title={`Open ${task.project_name || "project"}`}
            data-testid={`board-project-${task.task_id}`}
          >{task.project_name || "Project"}</button>
          {(task.client || task.address) && (
            <span className="truncate">· {task.client || ""}{task.client && task.address ? " / " : ""}{task.address || ""}</span>
          )}
        </p>
        <button onClick={() => onOpenTask(task)} className="text-left block w-full" data-testid={`board-open-${task.task_id}`}>
          <span className="text-sm font-semibold truncate block" style={{ color: "var(--twp-text)" }}>{task.name}</span>
        </button>
        <div className="flex items-center gap-3 mt-0.5 text-[11px]" style={{ color: "var(--twp-muted)" }}>
          {task.attachment_count > 0 && <span className="inline-flex items-center gap-0.5"><Paperclip className="h-3 w-3" />{task.attachment_count}</span>}
          {task.checklist_total > 0 && <span className="inline-flex items-center gap-0.5"><ListChecks className="h-3 w-3" />{task.checklist_done}/{task.checklist_total}</span>}
        </div>
      </div>

      {/* Status pill dropdown */}
      <Popover>
        <PopoverTrigger asChild>
          <button className="justify-self-start rounded-full px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wide" style={{ background: pill.bg, color: pill.text }} data-testid={`board-status-${task.task_id}`}>{pill.label}</button>
        </PopoverTrigger>
        <PopoverContent className="w-44 p-1" align="start">
          {STATUS_CHOICES.map((s) => (
            <button key={s} onClick={() => save({ status: s })} className="w-full text-left px-2 py-1.5 rounded-sm hover:bg-zinc-100 flex items-center gap-2 text-xs">
              <span className="h-2 w-2 rounded-full" style={{ background: (STATUS_PILL[s] || pill).text }} />
              {(STATUS_PILL[s] || {}).label || s}{task.status === s && <Check className="h-3 w-3 ml-auto" />}
            </button>
          ))}
        </PopoverContent>
      </Popover>

      {/* Priority flag */}
      <Popover>
        <PopoverTrigger asChild>
          <button className="hidden lg:inline-flex justify-self-start p-1" title={prio.label} data-testid={`board-prio-${task.task_id}`}>
            <Flag className="h-4 w-4" style={{ color: prio.color || "#D4D2CD", fill: prio.color || "transparent" }} />
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-36 p-1" align="start">
          {PRIORITY_CHOICES.map((p) => (
            <button key={p} onClick={() => save({ priority: p })} className="w-full text-left px-2 py-1.5 rounded-sm hover:bg-zinc-100 flex items-center gap-2 text-xs">
              <Flag className="h-3.5 w-3.5" style={{ color: PRIORITY_FLAG[p].color || "#D4D2CD", fill: PRIORITY_FLAG[p].color || "transparent" }} />
              {PRIORITY_FLAG[p].label}{task.priority === p && <Check className="h-3 w-3 ml-auto" />}
            </button>
          ))}
        </PopoverContent>
      </Popover>

      {/* Due date inline */}
      <div className="justify-self-start text-xs font-semibold">
        <DatePicker value={task.end_date || ""} onChange={(v) => save({ end_date: v })}>
          <button style={{ color: dueColor }} data-testid={`board-due-${task.task_id}`}>{task.end_date ? fmtDate(task.end_date) : "—"}</button>
        </DatePicker>
      </div>

      {/* Assignees */}
      <Popover>
        <PopoverTrigger asChild>
          <button className="justify-self-start flex items-center" data-testid={`board-assignee-${task.task_id}`}>
            {(task.assigned_to || []).length === 0 ? (
              <span className="text-xs" style={{ color: "var(--twp-muted)" }}>+ Assign</span>
            ) : (
              (task.assigned_to || []).slice(0, 4).map((uid, i) => {
                const m = members.find((x) => x.user_id === uid);
                return <Initials key={uid} id={uid} name={m?.name || "Unassigned"} i={i} />;
              })
            )}
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-52 p-1 max-h-64 overflow-y-auto" align="start">
          {members.map((m) => {
            const on = (task.assigned_to || []).includes(m.user_id);
            return (
              <button key={m.user_id} onClick={() => toggleAssignee(m.user_id)} className="w-full text-left px-2 py-1.5 rounded-sm hover:bg-zinc-100 flex items-center gap-2 text-xs">
                <Initials id={m.user_id} name={m.name} i={0} />
                <span className="truncate flex-1">{m.name}</span>{on && <Check className="h-3.5 w-3.5" />}
              </button>
            );
          })}
        </PopoverContent>
      </Popover>

      {/* Created by */}
      <span className="hidden lg:block text-xs truncate" style={{ color: "var(--twp-text-2)" }}>{task.created_by_name || "—"}</span>

      {/* Comments */}
      <button onClick={() => onOpenTask(task)} className="hidden lg:inline-flex items-center gap-1 text-xs justify-self-start" style={{ color: "var(--twp-text-2)" }} data-testid={`board-comments-${task.task_id}`}>
        <MessageSquare className="h-3.5 w-3.5" />{task.comment_count || 0}
      </button>

      {/* Folder tag */}
      <span className="hidden lg:inline-block justify-self-start text-[10px] font-semibold px-2 py-0.5 rounded-full truncate max-w-full" style={{ background: tag.bg, color: tag.text }}>{tag.label}</span>
    </div>
  );
}
